import { Skeleton, Typography } from "@mui/material";
import {
  useCallback,
  useMemo,
  useState,
  lazy,
  Suspense,
  useRef,
  useEffect,
} from "react";
import { SubscriptionUserInfoForm } from "../components/SubscriptionUserInfo/SubscriptionUserInfoForm";
import { SuccessSubscriptionMessage } from "../components/SuccessSubscriptionMessage";
import { Link } from "react-router-dom";
import { SubscriptionCheckoutForm } from "../components/SubscriptionCheckout/SubscriptionCheckoutForm";
import { SubscriptionFlowProps } from "../subscription.types";
import { SubscriptionContainer } from "../components/styles";
import { SubscriptionUserInfoSchema } from "../components/SubscriptionUserInfo/subscription-user-info-form-helpers";
import { useSaveSubscriptionFlowTransactionInfo } from "@queries/subscription-flow/useSaveSubscriptionFlowTransactionInfo";
import { SubscriptionFlowStepper } from "../components/SubscriptionFlowStepper";
import { usePatchSubscriptionFlowUserInfo } from "@queries/subscription-flow/usePatchSubscriptionFlowUserInfo";
import { appEventEmitter } from "@shared/events/app-event-emitter";
import {
  getLoginOptionById,
  isMlsSsoLoginOption,
  LOGIN_OPTION_STORAGE_KEY,
  LoginOption,
  useLoginOptions,
} from "@features/Auth/login-options-form-helpers";
import { REGISTRATION_OPTION_STORAGE_KEY } from "@pages/registration/subscription-registration-helpers";
import { useAppName } from "@shared/hooks/useAppName";
const PaymentProvider = lazy(() =>
  import("@features/Subscription/components/PaymentProvider").then(
    (module) => ({
      default: module.PaymentProvider,
    }),
  ),
);

const steps = ["Personal information", "Trial Setup"];

enum RegistrationStepsEnum {
  "PERSONAL_INFORMATION",
  "PAYMENT", // Trial Setup means Payment step
  "FINISH",
}

const setLoginOptionOnStorage = (availableLoginOptions: LoginOption[]) => {
  const mlsOption = sessionStorage.getItem(REGISTRATION_OPTION_STORAGE_KEY);
  if (mlsOption && getLoginOptionById(availableLoginOptions, mlsOption)) {
    localStorage.setItem(LOGIN_OPTION_STORAGE_KEY, mlsOption);
  }
  sessionStorage.removeItem(REGISTRATION_OPTION_STORAGE_KEY);
};

export const SubscriptionRegistrationFlow = (props: SubscriptionFlowProps) => {
  const {
    defaultValues,
    subscriptionId,
    subscriptionInfo,
    status,
    lastPaymentFailedReason,
    partner,
  } = props;

  const initialCurrentStep = useMemo(
    () => (subscriptionInfo?.clientSecret ? 1 : 0),
    [subscriptionInfo],
  );
  const [currentStep, setCurrentStep] =
    useState<RegistrationStepsEnum>(initialCurrentStep);
  const firstLoadRef = useRef(false);

  const isCollectPaymentDetailsRequired =
    subscriptionInfo &&
    subscriptionInfo.creditCardRequired &&
    subscriptionInfo.price !== 0;
  const { options: availableLoginOptions } = useLoginOptions();
  const appName = useAppName();

  const canEditEmail = partner ? !isMlsSsoLoginOption(partner) : false;

  const emitEvents = useCallback(
    (state: number) => {
      if (state === RegistrationStepsEnum.PERSONAL_INFORMATION) {
        appEventEmitter.emit({
          eventType: "sidekick-registration-personal-information-step",
        });
        return;
      }
      if (state === RegistrationStepsEnum.PAYMENT) {
        appEventEmitter.emit({
          eventType: "sidekick-registration-payment-step",
        });
        return;
      }
      if (state === RegistrationStepsEnum.FINISH) {
        appEventEmitter.emit({
          eventType: "sidekick-registration-complete-success",
        });
        setLoginOptionOnStorage(availableLoginOptions);
        return;
      }
    },
    [availableLoginOptions],
  );

  useEffect(() => {
    if (firstLoadRef.current) return;
    emitEvents(currentStep);
    firstLoadRef.current = true;
  }, [emitEvents, currentStep]);

  const handleChangeCurrentStep = useCallback(
    (state: number) => {
      setCurrentStep(state);
      emitEvents(state);
    },
    [emitEvents],
  );

  const onSuccessSubmitCheckoutForm = () => {
    handleChangeCurrentStep(RegistrationStepsEnum.FINISH);
  };

  const onBackClick = useCallback(() => {
    handleChangeCurrentStep(currentStep - 1);
  }, [handleChangeCurrentStep, currentStep]);

  const saveSubscriptionFlowTransactionInfo =
    useSaveSubscriptionFlowTransactionInfo({
      onSuccess: (data) => {
        if (data.paymentSkipped) {
          handleChangeCurrentStep(RegistrationStepsEnum.FINISH);
          return;
        }
        handleChangeCurrentStep(RegistrationStepsEnum.PAYMENT);
      },
    });

  const patchSubscriptionUserInfoMutation = usePatchSubscriptionFlowUserInfo();

  const onSubscriptionUserInfoFormSubmit = useCallback(
    async (data: SubscriptionUserInfoSchema) => {
      const stepUserInfo = partner?.subscriptionFlowConfig.stepUserInfo ?? [];
      const isReadOnly = (fieldName: string) =>
        stepUserInfo.find((f) => f.name === fieldName)?.readOnly ?? false;

      const params = {
        subscriptionId,
        user: {
          name: isReadOnly("name") ? undefined : (data.name?.trim() ?? ""),
          phoneNumber: data.phoneNumber ? data.phoneNumber.trim() : null,
          mlsId: isReadOnly("mlsId") ? undefined : (data.mlsId ?? ""),
          officeName: isReadOnly("officeName")
            ? undefined
            : (data.officeName?.trim() ?? ""),
        },
      };

      if (status === "payment-setup-pending") {
        await patchSubscriptionUserInfoMutation.mutateAsync(params);
        handleChangeCurrentStep(RegistrationStepsEnum.PAYMENT);
        return;
      }

      saveSubscriptionFlowTransactionInfo.mutate(params);
    },
    [
      subscriptionId,
      status,
      saveSubscriptionFlowTransactionInfo,
      handleChangeCurrentStep,
      patchSubscriptionUserInfoMutation,
      partner,
    ],
  );

  if (status === "trial-started" || status === "subscription-started") {
    if (saveSubscriptionFlowTransactionInfo.data?.paymentSkipped) {
      return (
        <SubscriptionContainer>
          <SuccessSubscriptionMessage
            message={`Welcome to ${appName}!`}
            description="Your registration is complete. Your subscription has started! An email with further instructions will follow shortly."
            redirectUrl="/"
            active={true}
          />
        </SubscriptionContainer>
      );
    }
    return (
      <SubscriptionContainer>
        <Typography variant="h5" textAlign="center" mt={6} mb={2}>
          You already have an active subscription. You can try to{" "}
          <Link to="/login">login</Link> or contact the support if you think
          that this is a mistake.
        </Typography>
      </SubscriptionContainer>
    );
  }

  if (status === "created") {
    return (
      <SubscriptionContainer>
        <Typography variant="h5" textAlign="center" mt={6}>
          You need to confirm your email address before you can continue.
        </Typography>
      </SubscriptionContainer>
    );
  }

  if (status !== "email-confirmed" && status !== "payment-setup-pending") {
    return (
      <SubscriptionContainer>
        <Typography variant="h5" textAlign="center" mt={6}>
          Something went wrong. Please try again later.
        </Typography>
      </SubscriptionContainer>
    );
  }

  return (
    <SubscriptionContainer>
      {isCollectPaymentDetailsRequired && currentStep < steps.length && (
        <SubscriptionFlowStepper currentStep={currentStep} steps={steps} />
      )}

      {currentStep === 0 && partner && (
        <SubscriptionUserInfoForm
          title="Personal details"
          subscriptionId={subscriptionId}
          subscriptionFlowStatus={status}
          defaultValues={defaultValues}
          onFormSubmit={onSubscriptionUserInfoFormSubmit}
          requestIsLoading={
            saveSubscriptionFlowTransactionInfo.isPending ||
            patchSubscriptionUserInfoMutation.isPending
          }
          subscriptionFlowConfig={partner.subscriptionFlowConfig}
          canEditEmail={canEditEmail}
          submitButtonText={
            isCollectPaymentDetailsRequired ? "Next" : "Start My Subscription"
          }
        />
      )}

      {currentStep === 1 &&
        !!subscriptionInfo &&
        !!subscriptionInfo.clientSecret && (
          <Suspense
            fallback={
              <Skeleton variant="rounded" sx={{ width: "100%", height: 300 }} />
            }
          >
            <PaymentProvider clientSecret={subscriptionInfo.clientSecret}>
              <SubscriptionCheckoutForm
                title="Payment method"
                hideUserInfoSummary
                defaultValues={defaultValues}
                subscriptionId={subscriptionId}
                subscriptionInfo={subscriptionInfo}
                lastPaymentFailedReason={lastPaymentFailedReason}
                onSubmit={onSuccessSubmitCheckoutForm}
                onBackClick={onBackClick}
              />
            </PaymentProvider>
          </Suspense>
        )}

      {currentStep === 2 && (
        <Skeleton variant="rounded" sx={{ width: "100%", height: 300 }} />
      )}

      <SuccessSubscriptionMessage
        message={`Welcome to ${appName}!`}
        description="Your registration is complete. Your free trial has started! An email with further instructions will follow shortly."
        redirectUrl="/"
        active={currentStep === RegistrationStepsEnum.FINISH}
      />
    </SubscriptionContainer>
  );
};
